import {
  Checkbox,
  Divider,
  Form,
  FormInstance,
  Input,
  InputNumber,
  message,
  Modal,
  Pagination,
  Radio,
  Space,
  Table,
  Typography,
} from 'antd';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import AppButton from '../../components/AppButton';
import AppCard from '../../components/AppCard';
import HeaderSection from '../../components/HeaderSection';
import AppLayout from '../layout/AppLayout';
import { httpRequest } from '../../helpers/api';
import { BaseResponseProps } from '../../types/config.type';
import SectionContent from '../../components/SectionContent';
import useDetailBreadcrumbs from '../../hooks/useDetailBreadcrumbs';
import { useDebounce } from 'use-debounce';
import { IHttpResponse, IPayloadPagination } from '../../helpers/pagination';
import { generateQueryString } from '../../helpers/generateQueryString';
import useFetchList from '../../hooks/useFetchList';
import { generateFormRules } from '../../helpers/formRules';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import {
  ResellerProperties,
  ResellerUserProperties,
  initialReseller,
} from '../../types/reseller.type';
import { CustomerProps } from '../../types/customer.type';

const { Text } = Typography;

interface IParams {
  resellerId: string;
}

interface ResponseProps extends BaseResponseProps {
  payload: Omit<ResellerProperties, 'createdAt' | 'updatedAt'>;
}

interface ResponseResellerProps extends BaseResponseProps {
  payload: ResellerProperties;
}

interface ResponseResellerUserProps extends BaseResponseProps {
  payload: ResellerUserProperties;
}

const ReferralEdit: React.FC = () => {
  const { setBreadcrumbDetails } = useDetailBreadcrumbs();
  const history = useHistory();
  const location = useLocation();
  const isEdit = location.pathname.includes('edit');
  const { resellerId } = useParams<IParams>();
  const formRef =
    React.useRef<
      FormInstance<Omit<ResellerProperties, 'createdAt' | 'updatedAt'>>
    >(null);

  const {
    isLoading: isLoadingAllCustomers,
    data: dataAllCustomers,
    pagination,
    setData,
    setSearch,
    setQuery,
    changePage,
    changeLimit,
    query,
  } = useFetchList<CustomerProps>({
    endpoint: 'customers',
    initialQuery: {
      isPublished: 'active',
      limit: 25,
    },
  });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isLoadingRemoveCustomer, setIsLoadingRemoveCustomer] =
    React.useState<boolean>(false);
  const [isLoadingAction, setIsLoadingAction] = React.useState<boolean>(false);
  const [reseller, setReseller] = React.useState<ResellerProperties>({
    ...initialReseller,
  });
  const [selectedCustomers, setSelectedCustomers] = React.useState<
    CustomerProps[]
  >([]);
  const [currResellerUser, setCurrResellerUser] = React.useState<
    ResellerUserProperties[]
  >([]);
  const [notAllowSave, setNotAllowSave] = React.useState<boolean>(false);
  const [isShowModalAddCustomer, setIsShowModalAddCustomer] =
    React.useState<boolean>(false);

  const createReseller = async (
    props: Omit<
      ResellerProperties,
      'createdAt' | 'updatedAt' | 'resellerId' | 'statusLoading'
    >
  ) => {
    try {
      setIsLoadingAction(true);

      const res = await httpRequest.post<ResponseResellerProps>('/resellers', {
        resellerName: props.resellerName,
        description: props.description || '',
        status: props.status,
      });

      if (res.data && res.data.payload) {
        const newCustomerMemberData = selectedCustomers.map((item) => ({
          resellerId: res.data.payload.resellerId,
          userId: item.customerId,
        }));

        await httpRequest.post<ResponseResellerUserProps>(
          '/reseller-users/bulk',
          {
            bulk: newCustomerMemberData,
          }
        );
      }

      message.success('Success create ' + props.resellerName);

      history.push('/reseller');
    } catch (error) {
      setIsLoadingAction(false);
    }
  };

  const updateReseller = async (
    props: Omit<ResellerProperties, 'createdAt' | 'updatedAt' | 'statusLoading'>
  ) => {
    try {
      setIsLoadingAction(true);

      await httpRequest.patch('/resellers/' + resellerId, {
        resellerName: props.resellerName,
        description: props.description,
        status: props.status,
      });

      const newCustomerMemberData = selectedCustomers.map((item) => {
        const found = currResellerUser.find(
          (a) => a.userId === item.customerId
        );
        return {
          id: found ? found.id : null,
          resellerId: resellerId,
          userId: item.customerId,
        };
      });

      await httpRequest.patch<ResponseResellerUserProps>(
        '/reseller-users/reseller/bulk',
        {
          bulk: newCustomerMemberData,
        }
      );
      console.log('success');
      message.success('Success update ' + props.resellerName + ' data');
      history.push('/reseller');
    } catch (error) {
      console.error('failed update reseller', error);
    } finally {
      setIsLoadingAction(false);
    }
  };

  React.useEffect(() => {
    if (resellerId) {
      const fetchReseller = async () => {
        try {
          setIsLoading(true);

          const res = await httpRequest.get<ResponseProps>(
            '/resellers/' + resellerId
          );

          setReseller(res.data.payload);
          const customers: any = [];
          if (
            res.data.payload.reseller_users &&
            res.data.payload.reseller_users.length > 0
          ) {
            for (const item of res.data.payload.reseller_users!) {
              customers.push(item.customer);
            }
            setSelectedCustomers(customers);
            setCurrResellerUser(res.data.payload.reseller_users);
          }

          const bcDetails = [
            {
              field: 'resellerId',
              value: resellerId,
              label: res.data.payload.resellerName,
            },
          ];
          setBreadcrumbDetails(bcDetails);
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
        }
      };

      fetchReseller();
    }
  }, [resellerId]);

  const handleSubmit = async (
    values: Omit<
      ResellerProperties,
      'createdAt' | 'updatedAt' | 'statusLoading' | 'isMealpack'
    >
  ) => {
    if (resellerId) {
      updateReseller(values);
    } else {
      createReseller(values);
    }
  };

  async function handleRemoveCustomer(data: CustomerProps) {
    try {
      setIsLoadingRemoveCustomer(true);
      if (currResellerUser.length > 0) {
        const found = currResellerUser.find(
          (item) => item.customer?.customerId === data.customerId
        );
        if (found) {
          await httpRequest.delete('reseller-users/' + found.id);
          message.success('Success remove ' + found.customer?.fullName);
          const newCurrResellerUser = currResellerUser.filter(
            (item) => item.id !== found.id
          );
          setCurrResellerUser(newCurrResellerUser);
        }
      }

      setSelectedCustomers((oldVal) =>
        oldVal.filter((item) => item.customerId !== data.customerId)
      );

      const newSelectedCustomersIds = selectedCustomers
        .filter((item) => item.customerId !== data.customerId)
        .map((item) => item.customerId);

      setQuery({
        ...query,
        notIncludeIds: newSelectedCustomersIds,
      });
    } catch (error) {
      console.error('failed error remove customer', error);
    } finally {
      setIsLoadingRemoveCustomer(false);
    }
  }

  const handleShowModalAddCustomer = () => {
    setIsShowModalAddCustomer(true);
    if (currResellerUser.length > 0) {
      setQuery({
        ...query,
        notIncludeIds: currResellerUser.map(
          (item) => item.customer?.customerId
        ),
      });
    }
  };

  function handleAddCustomer(data: CustomerProps) {
    const newSelectedCustomers = [...selectedCustomers, data];
    setSelectedCustomers(newSelectedCustomers);
    setQuery({
      ...query,
      notIncludeIds: newSelectedCustomers.map((item) => item.customerId),
    });
  }

  function handleConfirmAddCustomer() {
    setIsShowModalAddCustomer(false);
  }

  const columnsSelectedCustomer = [
    {
      title: 'Full name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (value: string) => <div>{value}</div>,
    },
    {
      title: 'Phone number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (value: string) => <div>{value}</div>,
    },
    {
      title: 'Email address',
      dataIndex: 'email',
      key: 'email',
      render: (value: string) => <div>{value}</div>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, item: CustomerProps) => (
        <AppButton
          type='primary'
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveCustomer(item)}
        />
      ),
    },
  ];

  const columnsAllCustomer = [
    {
      title: 'Full name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (value: string) => <div>{value}</div>,
    },
    {
      title: 'Phone number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (value: string) => <div>{value}</div>,
    },
    {
      title: 'Email address',
      dataIndex: 'email',
      key: 'email',
      render: (value: string) => <div>{value}</div>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, item: CustomerProps) => (
        <AppButton type='primary' onClick={() => handleAddCustomer(item)}>
          Add
        </AppButton>
      ),
    },
  ];

  return (
    <AppLayout>
      <HeaderSection
        icon='back'
        title={(resellerId ? 'Edit' : 'Add') + ' Reseller'}
        subtitle='Manage your reseller data'
        rightAction={
          <Space>
            <AppButton onClick={() => history.goBack()}>Cancel</AppButton>
            <AppButton
              disabled={notAllowSave}
              loading={isLoadingAction}
              type='primary'
              onClick={() => formRef?.current?.submit()}
            >
              Save
            </AppButton>
          </Space>
        }
      />
      <AppCard loading={isLoading}>
        <Form
          ref={formRef}
          name='profileForm'
          layout='vertical'
          onFinish={handleSubmit}
          initialValues={resellerId ? reseller : initialReseller}
          autoComplete='off'
        >
          <SectionContent
            groupTitle='Reseller Data'
            helpers={
              [
                // {
                //   title: 'Information',
                //   content:
                //     'Status In-Active will make this reseller unable to be used by customers',
                // },
              ]
            }
          >
            <Form.Item
              label='Reseller Group Name'
              name='resellerName'
              rules={generateFormRules('resellerName', ['required'])}
            >
              <Input placeholder='Enter reseller name' />
            </Form.Item>
            <Form.Item
              label='Status'
              name='status'
              rules={[
                {
                  required: true,
                  message: 'The Status is required.',
                },
              ]}
            >
              <Radio.Group value={reseller.status}>
                <Radio value={'active'}>Active</Radio>
                <Radio value={'inactive'}>In Active</Radio>
              </Radio.Group>
            </Form.Item>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle='Customer Data'
            helpers={
              [
                // {
                //   title: 'Information',
                //   content:
                //     'Customers that listed in here will have this reseller whenever they checkout',
                // },
              ]
            }
          >
            <Form.Item label='List of Customers'>
              {selectedCustomers.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #ddd',
                    padding: '15px 0',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>Data customer empty</div>
                </div>
              ) : (
                selectedCustomers.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #ddd',
                      padding: '15px 0',
                    }}
                  >
                    <div>{item.fullName}</div>
                    <AppButton
                      disabled={isLoadingRemoveCustomer}
                      type='primary'
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveCustomer(item)}
                    ></AppButton>
                  </div>
                ))
              )}

              <AppButton
                disabled={isLoadingRemoveCustomer}
                style={{ marginTop: 15 }}
                type='primary'
                onClick={() => handleShowModalAddCustomer()}
              >
                Add Customer
              </AppButton>
            </Form.Item>
          </SectionContent>
        </Form>
      </AppCard>

      <Modal
        title='Add Customer'
        visible={isShowModalAddCustomer}
        onOk={handleConfirmAddCustomer}
        onCancel={() => {
          setIsShowModalAddCustomer(false);
        }}
        okText={`Confirm ${selectedCustomers.length} Customer`}
        okButtonProps={{ type: 'primary' }}
        width={800}
        centered
      >
        <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 10 }}>
          Selected Customer
        </div>
        <Table
          loading={isLoading}
          columns={columnsSelectedCustomer}
          dataSource={selectedCustomers}
          pagination={false}
        />

        <div
          style={{
            fontWeight: 600,
            fontSize: 20,
            marginTop: 20,
            marginBottom: 10,
          }}
        >
          All Customer
        </div>
        <Input
          style={{ marginBottom: 10 }}
          size='large'
          placeholder='Search by Name or Email'
          prefix={<SearchOutlined />}
          allowClear
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table
          loading={isLoadingAllCustomers}
          columns={columnsAllCustomer}
          dataSource={dataAllCustomers}
          pagination={false}
        />
        <Pagination
          current={pagination.page}
          total={pagination.totalData}
          defaultPageSize={pagination.perPage}
          pageSize={pagination.perPage}
          onShowSizeChange={(_current, size) => {
            changeLimit(size);
          }}
          showSizeChanger={true}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
          }
          onChange={changePage}
        />
      </Modal>
    </AppLayout>
  );
};

export default ReferralEdit;
